// React and Library imports
import {
  lazy,
  Suspense,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Card, Container, Row } from "reactstrap";

// Hooks
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import useUser from "../../../../hooks/useUser";

// Components - Common
import Loader from "../../../../Layout/Loader/Loader";

// Redux - Slice
import {
  closeModal,
  openModal,
} from "../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";

// Services and Types
import { Nullable } from "../../../../Types/Common";

// Utils
import { findAndCategorizedAttachment } from "../../../../utils/fileUtils";

// Constants
import {
  icoRupeeIcon,
  icoRupeeTrueIcon,
  icoRupeePlusIcon,
  icoRupeeMinusIcon,
  TotalExpenses,
  WalletBalance,
  OpeningBalance,
  TotalAddedMoney,
  PettyCashExpenseModal,
  PettyCashExpenseAttachmentModal,
  pettyCashExpenseDeleteDialog,
} from "../../../../utils/Constant";

import "../pettyCash.scss";
import { useLocation, useParams } from "react-router-dom";
import { formatMYDate } from "../../../../utils/dateUtils";
import { IPettyCashExpenseTransactionsServiceResponse } from "../../../../Types/PettyCash";
import { ExtensionTypes } from "../../../reimbursement/types";
import { useModalSelector } from "../../../../hooks/useModalSelector";
import ManagerZoneTable from "./table/ManagerZoneTable";
import ExpenseAddModal from "./modals/ExpenseAddModal";
import ExpenseAttachmentCollectionModal from "./modals/ExpenseAttachmentCollectionModal";
import ExpenseSummaryCards from "./expenseSummaryCard";
import HeaderControls from "./headerControls/HeaderControls";
import { selectEmployeeSecondaryTabActive } from "../../../employeeManagement/redux/selector/EmployeeManagementSelector";
import { useEmployeeProfile } from "../../../../hooks/useEmployeeProfile";
import {
  useDeleteExpenseMutation,
  useExpenseCategoryListQuery,
  useExpenseListQuery,
} from "../../../../ReduxToolkit/api/pettyCash";
import { logErrors } from "../../../../utils/logErrors";
import { showApiResultAlert } from "../../../../utils/AlertManager/showApiAlert";

const ConfirmDeleteDialog = lazy(
  () => import("../../../../CommonElements/ConfirmDeleteDialog")
);

const ManagerZoneContainer = () => {
  const { centerId } = useParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [filterText, setFilterText] = useState<Nullable<string>>(null);
  const parsedCenterId = Number(centerId);

  const [selectedMonth, setSelectedMonth] = useState<any>(
    new Date().toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    })
  );

  const [attachmentCollection, setAttachmentCollection] =
    useState<ExtensionTypes>({
      img: [],
      pdf: [],
      excel: [],
    });

  const loginEmployee = useUser();

  const { employeeProfileDetails } = useEmployeeProfile();
  const secondaryTabActive = useAppSelector(selectEmployeeSecondaryTabActive);
  const { isOpen, modalType, selectedId } = useModalSelector();

  const employeeJobInfo =
    employeeProfileDetails && employeeProfileDetails.jobInformation;
  const employeeCenterId = employeeJobInfo && Number(employeeJobInfo.centerId);
  const employeeCenterName = employeeJobInfo && employeeJobInfo?.location;

  const expenseCenterId = parsedCenterId || employeeCenterId;

  const { data: pettyCashExpenseCategoriesList } = useExpenseCategoryListQuery(
    undefined,
    {
      skip: !expenseCenterId,
    }
  );
  const shouldFetch =
    (expenseCenterId &&
      selectedMonth &&
      Number(secondaryTabActive) === 303 &&
      location.pathname === "/user-management/user") ||
    expenseCenterId;

  const { data: pettyCashExpenseList, isLoading: pettyCashExpenseListLoading } =
    useExpenseListQuery(
      {
        center_id: Number(expenseCenterId),
        month: formatMYDate(new Date(selectedMonth)),
      },
      {
        skip: !shouldFetch,
      }
    );

  const [onDeleteExpense, { reset: resetDeleteExpense }] =
    useDeleteExpenseMutation();

  const handleOnChangeMonth = useCallback(
    async (date: Date) => {
      setSelectedMonth(date);
    },
    [dispatch, expenseCenterId]
  );

  const pettyCashDetails = useMemo(() => {
    if (!pettyCashExpenseList?.success) return [];
    const {
      opening_balance,
      wallet_balance,
      current_month_credit,
      current_month_debit,
    } = pettyCashExpenseList;
    return [
      {
        label: OpeningBalance,
        value: opening_balance,
        iconClassname: `${icoRupeeIcon} text-primary f-28`,
        alt: "rupee-icon",
      },
      {
        label: WalletBalance,
        value: wallet_balance,
        iconClassname: `${icoRupeeTrueIcon} text-primary f-28`,
        alt: "rupee-true-icon",
      },
      {
        label: TotalAddedMoney,
        value: current_month_credit,
        iconClassname: `${icoRupeePlusIcon} text-primary f-28`,
        alt: "rupee-plus-icon",
      },
      {
        label: TotalExpenses,
        value: current_month_debit,
        iconClassname: `${icoRupeeMinusIcon} text-primary f-28`,
        alt: "rupee-minus-icon",
      },
    ];
  }, [pettyCashExpenseList]);

  const handleOpenAttachmentModal = useCallback(
    (expenseData: IPettyCashExpenseTransactionsServiceResponse) => {
      if (expenseData?.bill_url) {
        const attachments = findAndCategorizedAttachment(expenseData.bill_url);
        if (attachments) setAttachmentCollection(attachments);
        dispatch(openModal({ modalType: PettyCashExpenseAttachmentModal }));
      }
    },
    [dispatch]
  );

  const handleOpenDialog = useCallback(
    (id: number) =>
      dispatch(
        openModal({ modalType: pettyCashExpenseDeleteDialog, selectedId: id })
      ),
    [dispatch]
  );

  const handleDeletePettyCashExpense = useCallback(async () => {
    if (!selectedId) return logErrors("selected id is missing in handleDeletePettyCashExpense");

    try {
      const deleteExpenseResponse = await onDeleteExpense({
        id: Number(selectedId),
      });

      showApiResultAlert(
        deleteExpenseResponse,
        {
          title: "Delete Expense Info",
        },
        () => resetDeleteExpense()
      );

      dispatch(closeModal());
    } catch (err) {
      logErrors("Api Error", err);
    }
  }, [selectedId, onDeleteExpense]);

  // Filter the leaveData based on filterText
  const filteredData = useMemo(
    () =>
      pettyCashExpenseList &&
      pettyCashExpenseList?.transactions &&
      pettyCashExpenseList?.transactions.length > 0 &&
      pettyCashExpenseList.transactions
        .slice()
        .reverse()
        .filter(
          (row) =>
            !filterText ||
            row.date?.toLowerCase().includes(filterText.toLowerCase()) ||
            row?.balance?.includes(filterText) ||
            row.category_name?.toLowerCase().includes(filterText) ||
            row.user_name?.toLowerCase().includes(filterText) ||
            row.details?.toLowerCase().includes(filterText)
        ),
    [pettyCashExpenseList, filterText]
  );

  return (
    <div>
      <Container fluid className="ecommerce-dashboard ">
        <ExpenseSummaryCards
          pettyCashDetails={pettyCashDetails}
          pettyCashListLoading={pettyCashExpenseListLoading}
        />
        <Card className="mb-4 p-4">
          <HeaderControls
            selectedMonth={selectedMonth}
            onChangeMonth={handleOnChangeMonth}
            centerName={
              location?.state?.expenseCenterName || employeeCenterName
            }
          />
          <Row>
            <ManagerZoneTable
              location={location}
              filterText={filterText ?? ""}
              filteredData={filteredData || []}
              setFilterText={setFilterText}
              openDialog={handleOpenDialog}
              expenseListLoading={pettyCashExpenseListLoading}
              openAttachmentModal={handleOpenAttachmentModal}
            />
          </Row>
        </Card>
      </Container>
      <Suspense fallback={<Loader />}>
        {modalType === PettyCashExpenseModal &&
          isOpen &&
          pettyCashExpenseCategoriesList && (
            <ExpenseAddModal
              isOpen={isOpen}
              userId={loginEmployee?.id ?? 0}
              onClose={() => dispatch(closeModal())}
              expenseCategoryTypeOptions={pettyCashExpenseCategoriesList}
            />
          )}
      </Suspense>
      <Suspense fallback={<Loader />}>
        {modalType === PettyCashExpenseAttachmentModal && isOpen && (
          <ExpenseAttachmentCollectionModal
            isOpen={isOpen}
            attachmentCollection={attachmentCollection}
            onClose={() => dispatch(closeModal())}
          />
        )}
      </Suspense>
      <Suspense fallback={<Loader />}>
        {modalType === "pettyCashExpenseDeleteDialog" && isOpen && (
          <ConfirmDeleteDialog
            isOpen={isOpen}
            size="sm"
            label="Are you sure want to remove ?"
            handleToggle={() => dispatch(closeModal())}
            handleClickFirstBtn={handleDeletePettyCashExpense}
          />
        )}
      </Suspense>
    </div>
  );
};

export default ManagerZoneContainer;
